import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  StickyNote,
  Plus,
  Pin,
  Trash2,
  Bell,
  X,
  Clock,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_SWATCHES = [
  { bg: "#FEF3C7", border: "#FDE68A" }, // amber
  { bg: "#DCFCE7", border: "#BBF7D0" }, // green
  { bg: "#DBEAFE", border: "#BFDBFE" }, // blue
  { bg: "#FCE7F3", border: "#FBCFE8" }, // pink
  { bg: "#E0E7FF", border: "#C7D2FE" }, // indigo
  { bg: "#F3F4F6", border: "#E5E7EB" }, // grey
];

const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  if (mins < 60) return diff < 0 ? `${mins}m ago` : `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return diff < 0 ? `${hrs}h ago` : `in ${hrs}h`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * NotesWidget — a personal note-taker + reminder panel.
 * - Quick capture with a single-line input
 * - Each note can be pinned, colored, and given a `remind_at` (server will fire a notification)
 * - Meant to sit beside "My Assigned Tasks" on every role's dashboard
 */
const NotesWidget = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [colorIdx, setColorIdx] = useState(0);
  const [reminderFor, setReminderFor] = useState(null); // note being edited
  const [reminderAt, setReminderAt] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notes`);
      setNotes(r.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s so fired reminders clear
    return () => clearInterval(id);
  }, [load]);

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      const swatch = COLOR_SWATCHES[colorIdx];
      const r = await axios.post(`${API}/notes`, { content: draft.trim(), color: swatch.bg });
      setNotes([r.data, ...notes]);
      setDraft("");
      // rotate color swatch for variety
      setColorIdx((i) => (i + 1) % COLOR_SWATCHES.length);
    } catch {
      toast.error("Couldn't save note");
    }
  };

  const togglePin = async (id) => {
    try {
      const r = await axios.post(`${API}/notes/${id}/toggle-pin`);
      setNotes(notes.map((n) => (n.id === id ? r.data : n)).sort(sortByPin));
    } catch {
      toast.error("Couldn't toggle pin");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/notes/${id}`);
      setNotes(notes.filter((n) => n.id !== id));
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const sortByPin = (a, b) => {
    if (a.pinned !== b.pinned) return b.pinned - a.pinned;
    return new Date(b.updated_at) - new Date(a.updated_at);
  };

  const openReminderFor = (note) => {
    setReminderFor(note);
    // prefill: existing remind_at (converted to local datetime-local format) or +1h default
    const base = note.remind_at ? new Date(note.remind_at) : new Date(Date.now() + 60 * 60 * 1000);
    const local = new Date(base.getTime() - base.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setReminderAt(local);
  };

  const saveReminder = async () => {
    if (!reminderAt) return toast.error("Pick a date & time");
    try {
      const whenUtc = new Date(reminderAt).toISOString();
      const r = await axios.put(`${API}/notes/${reminderFor.id}`, { remind_at: whenUtc });
      setNotes(notes.map((n) => (n.id === reminderFor.id ? r.data : n)).sort(sortByPin));
      toast.success("Reminder set");
      setReminderFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't set reminder");
    }
  };

  const clearReminder = async () => {
    try {
      const r = await axios.put(`${API}/notes/${reminderFor.id}`, { clear_remind_at: true });
      setNotes(notes.map((n) => (n.id === reminderFor.id ? r.data : n)).sort(sortByPin));
      toast.success("Reminder cleared");
      setReminderFor(null);
    } catch {
      toast.error("Couldn't clear reminder");
    }
  };

  return (
    <div className="stat-card h-full flex flex-col" data-testid="notes-widget">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <StickyNote size={18} className="text-amber-500" />
          Notes & Reminders
        </h2>
        <span className="text-xs text-slate-400">{notes.length}</span>
      </div>

      {/* Quick add */}
      <form onSubmit={quickAdd} className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Jot a quick note…"
            className="w-full border border-slate-200 rounded-lg pl-3 pr-12 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/30 focus:border-prepify-500"
            data-testid="notes-input"
          />
          <button
            type="button"
            onClick={() => setColorIdx((i) => (i + 1) % COLOR_SWATCHES.length)}
            title="Cycle color"
            className="absolute right-1 top-1 w-7 h-7 rounded-md border border-slate-200"
            style={{ background: COLOR_SWATCHES[colorIdx].bg }}
            data-testid="notes-color-swatch"
          />
        </div>
        <button
          type="submit"
          disabled={!draft.trim()}
          className="inline-flex items-center gap-1 bg-slate-900 hover:bg-prepify-600 disabled:opacity-40 text-white text-sm font-medium px-3 py-2 rounded-lg transition"
          data-testid="notes-add-btn"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      {/* List */}
      <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-2" style={{ maxHeight: 360 }} data-testid="notes-list">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        ) : notes.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <StickyNote size={24} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No notes yet. Capture a thought above.</p>
          </div>
        ) : (
          notes.map((n) => {
            const dueSoon = n.remind_at && new Date(n.remind_at) <= new Date(Date.now() + 60 * 60 * 1000);
            const overdue = n.remind_at && new Date(n.remind_at) <= new Date() && !n.reminder_fired;
            return (
              <div
                key={n.id}
                className="group relative rounded-lg border p-3 hover:shadow-sm transition-shadow"
                style={{ background: n.color || "#FEF3C7", borderColor: "rgba(0,0,0,0.06)" }}
                data-testid={`note-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {n.pinned && <Pin size={12} className="text-slate-600 mt-1 flex-shrink-0" fill="currentColor" />}
                  <div className="flex-1 min-w-0">
                    {n.title && <div className="text-sm font-semibold text-slate-900 mb-0.5">{n.title}</div>}
                    <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.content}</div>
                    {n.remind_at && (
                      <div className={`inline-flex items-center gap-1 text-[11px] font-medium mt-2 px-2 py-0.5 rounded-full ${
                        overdue ? "bg-red-100 text-red-700" : dueSoon ? "bg-amber-200 text-amber-800" : "bg-white/70 text-slate-700"
                      }`}>
                        {n.reminder_fired ? <Check size={10} /> : <Bell size={10} />}
                        {n.reminder_fired ? "Reminder sent" : formatRelative(n.remind_at)}
                      </div>
                    )}
                  </div>
                  {/* Actions — visible on hover */}
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => togglePin(n.id)} title={n.pinned ? "Unpin" : "Pin"} className="p-1 hover:bg-white/60 rounded" data-testid={`note-pin-${n.id}`}>
                      <Pin size={12} fill={n.pinned ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => openReminderFor(n)} title="Set reminder" className="p-1 hover:bg-white/60 rounded" data-testid={`note-reminder-${n.id}`}>
                      <Clock size={12} />
                    </button>
                    <button onClick={() => remove(n.id)} title="Delete" className="p-1 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`note-delete-${n.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reminder modal */}
      {reminderFor && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setReminderFor(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2"><Bell size={16} className="text-amber-500" /> Set reminder</h3>
              <button onClick={() => setReminderFor(null)} className="p-1 hover:bg-slate-100 rounded" aria-label="Close"><X size={16} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3 line-clamp-2">{reminderFor.content}</p>
            <label className="block text-xs font-medium text-slate-600 mb-1">Remind me at</label>
            <input
              type="datetime-local"
              value={reminderAt}
              onChange={(e) => setReminderAt(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/30"
              data-testid="notes-reminder-datetime"
            />
            <p className="text-[11px] text-slate-400 mt-1.5">You'll get a notification in the bell at the top when the time hits.</p>
            <div className="flex justify-between gap-2 mt-4">
              {reminderFor.remind_at && (
                <button onClick={clearReminder} className="text-xs text-red-600 hover:underline" data-testid="notes-reminder-clear">Clear reminder</button>
              )}
              <div className="ml-auto flex gap-2">
                <button onClick={() => setReminderFor(null)} className="text-sm text-slate-600 hover:bg-slate-100 px-3 py-1.5 rounded-lg">Cancel</button>
                <button onClick={saveReminder} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-prepify-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg" data-testid="notes-reminder-save">
                  <Bell size={12} /> Set
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesWidget;
